/**
 * Uit Dienst Dialog (Epic 5 — Uit-dienst-registratie, Story 5.2)
 *
 * Dialoog "Uit dienst melden" (UX-spec S6/UX-DR6): datum-picker + vaste
 * gevolgen-lijst + verplichte bevestigings-checkbox vóórdat de primaire knop
 * actief wordt. Dit project heeft geen shadcn/ui (zie `PackageChangeDialog.tsx`
 * Dev Notes) — zelfde custom-modal-patroon (overlay + Escape-to-close).
 *
 * AD-2/AD-7 (bindend): de server is autoritatief voor alle spelregels (o.a.
 * de AD-7-maandregel voor het sluiten van het tijdvak) — dit component toont
 * de gevolgen vóór (vaste opsomming) maar herhaalt geen server-logica als
 * bron van waarheid.
 *
 * Designsysteem (NFR-1, bindend): "Uit dienst melden" is GEEN fout — de knop
 * is secundair/teal, nooit rood. Rood (`#D64545`) uitsluitend bij een echte
 * servervalidatiefout na een opslagpoging (UX-DR8).
 */
import { useEffect, useState } from 'react';
import { useSetUitDienst } from '../useEmployees';
import { ApiRequestError } from '../../../lib/api';

const GEVOLGEN_TEXT =
  'Per deze datum: kerntaak-toewijzingen eindigen · verdwijnt uit actieve lijsten, roosters en planning · ' +
  'doet niet mee aan nieuwe beoordelingsrondes · Nmbrs-koppeling wordt inactief gemarkeerd. Historie blijft volledig inzichtelijk.';

interface UitDienstDialogProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  onSaved?: () => void;
}

export function UitDienstDialog({ isOpen, onClose, employeeId, onSaved }: UitDienstDialogProps) {
  const setUitDienst = useSetUitDienst(employeeId);

  const [uitDienstDatum, setUitDienstDatum] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setUitDienstDatum('');
    setConfirmed(false);
    setSaveAttempted(false);
    setServerError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Review-fix: geen Escape-close terwijl een opslag-poging nog in flight
    // is -- anders sluit de dialoog visueel terwijl de registratie op de
    // achtergrond alsnog voltooit/faalt, zonder dat de gebruiker dat nog ziet.
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !setUitDienst.isPending) onClose();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [isOpen, onClose, setUitDienst.isPending]);

  if (!isOpen) return null;

  const hasDate = uitDienstDatum.trim().length > 0;
  const isValid = hasDate && confirmed;

  async function handleSubmit() {
    // Review-fix: re-entry-guard -- `aria-disabled` (bewust geen native
    // `disabled`, zie hieronder) blokkeert een klik niet, dus zonder deze
    // guard kan een snelle dubbele klik vóór de eerste re-render twee
    // gelijktijdige POSTs afvuren.
    if (setUitDienst.isPending) return;
    setSaveAttempted(true);
    setServerError(null);
    if (!isValid) return;

    try {
      await setUitDienst.mutateAsync({ uitDienstDatum });
      onSaved?.();
      onClose();
    } catch (error) {
      setServerError(error instanceof ApiRequestError ? error.message : 'Er is een onbekende fout opgetreden');
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="uit-dienst-dialog-overlay">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="uit-dienst-dialog"
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
      >
        <h2 className="text-lg font-medium text-gray-900 mb-4">Uit dienst melden</h2>

        {serverError && (
          <div data-testid="uit-dienst-server-error" className="mb-4 px-3 py-2 rounded-md bg-[#D64545]/10 text-[#D64545] text-sm">
            {serverError}
          </div>
        )}

        {/* Datumveld */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="uit-dienst-datum-input">
            Uit dienst per
          </label>
          <input
            id="uit-dienst-datum-input"
            type="date"
            data-testid="uit-dienst-datum-input"
            value={uitDienstDatum}
            onChange={(e) => setUitDienstDatum(e.target.value)}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
          />
          {saveAttempted && !hasDate && <p className="mt-1 text-xs text-gray-500">Kies een datum.</p>}
        </div>

        {/* Gevolgen-lijst (vaste opsomming, geen verrassingen) */}
        <div data-testid="uit-dienst-gevolgen-lijst" className="mb-4 flex gap-2 px-3 py-3 rounded-md bg-gray-50 border border-[#E2E8F0]">
          <svg
            className="h-5 w-5 flex-shrink-0 text-[#54949E]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-700">{GEVOLGEN_TEXT}</p>
        </div>

        {/* Bevestigings-checkbox */}
        <div className="mb-6">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="uit-dienst-confirm-checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 rounded border-[#E2E8F0] text-[#54949E] focus:ring-[#54949E]"
            />
            <span className="text-sm text-gray-700">Ik heb de gevolgen gecontroleerd</span>
          </label>
          {/* Review-fix: symmetrisch aan de datum-hint hierboven -- eerder
              werd alleen een lege datum na een save-poging toegelicht. */}
          {saveAttempted && !confirmed && (
            <p data-testid="uit-dienst-confirm-hint" className="mt-1 text-xs text-gray-500">
              Bevestig dat u de gevolgen heeft gecontroleerd.
            </p>
          )}
        </div>

        {/* Acties */}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="uit-dienst-cancel"
            onClick={onClose}
            disabled={setUitDienst.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 rounded-md border border-[#E2E8F0] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuleren
          </button>
          <span className="relative group">
            {/*
              Zelfde patroon als PackageChangeDialog: bewust GEEN native
              `disabled` -- een echt disabled element vuurt geen click-event,
              waardoor "poging tot opslaan"-validatieguidance (UX-DR8) nooit
              zou kunnen tonen. Zacht disabled via `aria-disabled` +
              vroege return in `handleSubmit`.
            */}
            <button
              type="button"
              data-testid="uit-dienst-submit"
              aria-disabled={!isValid || setUitDienst.isPending}
              onClick={handleSubmit}
              title={!isValid ? 'Kies een datum en bevestig dat u de gevolgen heeft gecontroleerd' : undefined}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md bg-[#2F5A7A] hover:bg-[#2F5A7A]/90 ${
                !isValid || setUitDienst.isPending ? 'opacity-40 cursor-not-allowed' : ''
              }`}
            >
              {setUitDienst.isPending ? 'Bezig...' : 'Uit dienst melden'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
